import React, { useState, useMemo } from 'react';

export interface BarChartData {
  label: string;
  value: number;
  color?: string;
}

export interface BarChartAnswerState {
  selectedIndices: number[];
  isCorrect?: boolean;
  isSubmitted: boolean;
}

interface BarChartProps {
  data: BarChartData[];
  mode?: 'display' | 'interactive';
  maxSelections?: number;
  scaleMax?: number;
  scaleStep?: number;
  answerState?: BarChartAnswerState;
  correctIndices?: number[];
  onSelectionChange?: (indices: number[]) => void;
  yAxisLabel?: string;
  xAxisLabel?: string;
}

const DEFAULT_COLORS = [
  '#f59e0b', // amber
  '#8b5cf6', // violet
  '#ef4444', // red
  '#3b82f6', // blue
  '#06b6d4', // cyan
  '#10b981', // emerald
  '#f97316', // orange
  '#ec4899', // pink
];

const BarChart: React.FC<BarChartProps> = ({
  data,
  mode = 'display',
  maxSelections = 1,
  scaleMax,
  scaleStep = 100,
  answerState = { selectedIndices: [], isSubmitted: false },
  correctIndices = [],
  onSelectionChange,
  yAxisLabel,
  xAxisLabel,
}) => {
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  // Chart dimensions
  const width = 500;
  const height = 280;
  const padding = { top: 20, right: 20, bottom: 40, left: 50 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;

  const computedMax = useMemo(() => {
    if (scaleMax !== undefined) return scaleMax;
    const maxValue = Math.max(...data.map(d => d.value), 0);
    const calculated = Math.ceil(maxValue / scaleStep) * scaleStep;
    return calculated === 0 ? scaleStep : calculated;
  }, [data, scaleMax, scaleStep]);

  const scaleValues = useMemo(() => {
    const values: number[] = [];
    for (let i = 0; i <= computedMax; i += scaleStep) {
      values.push(i);
    }
    return values;
  }, [computedMax, scaleStep]);

  const isInteractiveAndNotSubmitted = mode === 'interactive' && !answerState.isSubmitted;

  const handleBarClick = (index: number) => {
    if (!isInteractiveAndNotSubmitted || !onSelectionChange) return;

    const currentSelections = [...answerState.selectedIndices];
    const selectionIndex = currentSelections.indexOf(index);

    if (selectionIndex > -1) {
      currentSelections.splice(selectionIndex, 1);
    } else {
      if (currentSelections.length >= maxSelections) {
        currentSelections.shift();
      }
      currentSelections.push(index);
    }

    onSelectionChange(currentSelections);
  };

  const getBarColor = (index: number, isSelected: boolean, isCorrectAnswer: boolean) => {
    const baseColor = data[index].color || DEFAULT_COLORS[index % DEFAULT_COLORS.length];

    if (answerState.isSubmitted && isSelected) {
      return isCorrectAnswer ? '#10b981' : '#ef4444';
    }

    return baseColor;
  };

  // Calculate bar positions
  const barWidth = Math.min(50, (chartWidth / data.length) * 0.6);
  const barGap = (chartWidth - barWidth * data.length) / (data.length + 1);

  return (
    <div className="w-full flex flex-col items-center">
      {/* Y-axis label */}
      {yAxisLabel && (
        <div className="text-xs mb-2" style={{ color: '#94a3b8' }}>
          {yAxisLabel}
        </div>
      )}

      <svg width={width} height={height} className="overflow-visible">
        {/* Grid lines */}
        {scaleValues.map((value) => {
          const y = padding.top + chartHeight - (value / computedMax) * chartHeight;
          return (
            <g key={value}>
              <line
                x1={padding.left}
                y1={y}
                x2={padding.left + chartWidth}
                y2={y}
                stroke="rgba(255, 255, 255, 0.1)"
                strokeWidth="1"
              />
              <text
                x={padding.left - 10}
                y={y}
                textAnchor="end"
                dominantBaseline="middle"
                fill="#64748b"
                fontSize="12"
              >
                {value}
              </text>
            </g>
          );
        })}

        {/* X-axis line */}
        <line
          x1={padding.left}
          y1={padding.top + chartHeight}
          x2={padding.left + chartWidth}
          y2={padding.top + chartHeight}
          stroke="rgba(255, 255, 255, 0.2)"
          strokeWidth="1"
        />

        {/* Bars */}
        {data.map((item, index) => {
          const barHeight = (item.value / computedMax) * chartHeight;
          const x = padding.left + barGap + index * (barWidth + barGap);
          const y = padding.top + chartHeight - barHeight;

          const isSelected = answerState.selectedIndices.includes(index);
          const isCorrectAnswer = correctIndices.includes(index);
          const isHovered = hoveredIndex === index;
          const barColor = getBarColor(index, isSelected, isCorrectAnswer);

          return (
            <g key={index}>
              {/* Bar */}
              <rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                rx={4}
                fill={barColor}
                opacity={isSelected || isHovered ? 1 : 0.8}
                stroke={isSelected ? (answerState.isSubmitted ? (isCorrectAnswer ? '#10b981' : '#ef4444') : '#fff') : 'none'}
                strokeWidth={isSelected ? 3 : 0}
                className={`transition-all duration-200 ${isInteractiveAndNotSubmitted ? 'cursor-pointer' : ''}`}
                onClick={() => handleBarClick(index)}
                onMouseEnter={() => isInteractiveAndNotSubmitted && setHoveredIndex(index)}
                onMouseLeave={() => setHoveredIndex(null)}
              />

              {/* Selection indicator */}
              {answerState.isSubmitted && isSelected && (
                <g>
                  <circle
                    cx={x + barWidth / 2}
                    cy={y - 15}
                    r={10}
                    fill={isCorrectAnswer ? '#10b981' : '#ef4444'}
                  />
                  <text
                    x={x + barWidth / 2}
                    y={y - 15}
                    textAnchor="middle"
                    dominantBaseline="central"
                    fill="#fff"
                    fontSize="12"
                    fontWeight="bold"
                  >
                    {isCorrectAnswer ? '✓' : '✗'}
                  </text>
                </g>
              )}

              {/* Label */}
              <text
                x={x + barWidth / 2}
                y={padding.top + chartHeight + 20}
                textAnchor="middle"
                fill={isSelected ? '#fff' : '#94a3b8'}
                fontSize="12"
                fontWeight={isSelected ? 'bold' : 'normal'}
              >
                {item.label}
              </text>
            </g>
          );
        })}
      </svg>

      {/* X-axis label */}
      {xAxisLabel && (
        <div className="text-xs mt-2" style={{ color: '#94a3b8' }}>
          {xAxisLabel}
        </div>
      )}
    </div>
  );
};

export default BarChart;
